import axios from "axios";
import moment from "moment";
import React, { useState } from "react";
import { MdOutlineBedtime, MdOutlineRestaurant, MdOutlineWc } from "react-icons/md";
import { useQuery } from "react-query";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import BirthdayFeed from "../../../components/sections/BirthdayFeed";
import AttendanceAppreciationBanner from "../../../components/cards/AttendanceAppreciationBanner";
import ClassAnnouncementFeed from "../../../components/sections/ClassAnnouncementFeed";
import FeedSection from "../../../components/sections/FeedSection";
import ParentMainProfile from "../../../components/sections/ParentMainProfile";
import { serverUrl } from "../../../nestserver";
import { selectSchoolId } from "../../../redux/slices/auth.slice";

const DAYCARE_TYPE_META = {
  meal: { color: "#f97316", bg: "#fff1e6", label: "Meal", icon: MdOutlineRestaurant },
  loo: { color: "#2563eb", bg: "#eff6ff", label: "Loo", icon: MdOutlineWc },
  sleep: { color: "#7c3aed", bg: "#f5f3ff", label: "Sleep", icon: MdOutlineBedtime },
};

const daycareEntrySummary = (entry) => {
  const parts = [];
  if (entry.type === "meal") {
    if (entry.meal_slot) parts.push(entry.meal_slot);
    if (entry.amount) parts.push(`Ate: ${entry.amount}`);
  } else if (entry.type === "loo") {
    if (entry.loo_kind) parts.push(entry.loo_kind);
  } else if (entry.type === "sleep") {
    parts.push(
      entry.sleep_end && entry.duration_min
        ? `${entry.duration_min} min nap`
        : "Nap started",
    );
  }
  return parts.join(" • ");
};

const authHeaders = () => ({
  Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
});

/**
 * Today's day-care log entries, across all of the parent's children, shown
 * at the top of the Class Update tab — replaces the standalone "Day Care
 * Activity" menu tile. Only fetched for Montessori schools; the full
 * per-child Timeline/History screens are still reachable by tapping a card.
 */
const TodayDaycareActivity = ({ isMontessoriEnabled }) => {
  const navigate = useNavigate();

  async function fetchChildren() {
    const res = await axios.get(`${serverUrl}/students/by-parent/`, {
      headers: authHeaders(),
    });
    return res.data.result || [];
  }
  const children = useQuery("students/by-parent", fetchChildren, {
    enabled: isMontessoriEnabled,
  });

  const today = moment().format("YYYY-MM-DD");
  async function fetchTodayEntries() {
    const kids = children.data || [];
    if (kids.length === 0) return [];
    const responses = await Promise.all(
      kids.map((child) =>
        axios
          .get(
            `${serverUrl}/montessori/daily-activity/student/${child._id}?date=${today}`,
            { headers: authHeaders() },
          )
          .then((res) => ({ child, entries: res.data?.result?.data || [] }))
          .catch(() => ({ child, entries: [] })),
      ),
    );
    return responses
      .flatMap(({ child, entries }) => entries.map((entry) => ({ entry, child })))
      .sort((a, b) => new Date(b.entry.occurred_at) - new Date(a.entry.occurred_at));
  }
  const todayEntries = useQuery(
    ["daycare-today", children.data?.map((c) => c._id).join(","), today],
    fetchTodayEntries,
    { enabled: isMontessoriEnabled && !!children.data?.length },
  );

  if (!isMontessoriEnabled || !todayEntries.data?.length) return null;

  return (
    <div className="mb-6">
      <div className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
        Today&apos;s Day Care Activity
      </div>
      <div className="space-y-3">
        {todayEntries.data.map(({ entry, child }) => {
          const meta = DAYCARE_TYPE_META[entry.type];
          const Icon = meta?.icon;
          return (
            <div
              key={entry._id}
              onClick={() => navigate("/dashboard/parent/menu/daily-activity?tab=timeline")}
              className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-3 cursor-pointer hover:shadow-md transition-all"
            >
              <div
                className="rounded-full flex items-center justify-center shrink-0"
                style={{ width: 40, height: 40, background: meta?.bg, color: meta?.color }}
              >
                {Icon && <Icon size={20} />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-gray-800">
                  {child?.first_name} — {meta?.label}
                  <span className="text-gray-400 font-normal ml-2">
                    {moment(entry.occurred_at).format("h:mm A")}
                  </span>
                </div>
                {daycareEntrySummary(entry) && (
                  <div className="text-sm text-gray-500 truncate">
                    {daycareEntrySummary(entry)}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const Index = () => {
  const [activeTab, setActiveTab] = useState("school-post");
  const schoolId = useSelector(selectSchoolId);

  async function fetchSchool() {
    const res = await axios.get(`${serverUrl}/school/${schoolId}`, {
      headers: authHeaders(),
    });
    return res.data;
  }
  const school = useQuery(["school", schoolId], fetchSchool, {
    enabled: !!schoolId,
  });
  const isMontessoriEnabled = school?.data?.result?.data?.is_montessori_enabled === true;

  async function getAnnouncement() {
    const data = await axios({
      method: "get",
      url: serverUrl + "/announcement",
      headers: {
        Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
      },
    });

    return data.data;
  }

  const announcement = useQuery("announcement", getAnnouncement);

  async function getUnreadCount() {
    const data = await axios({
      method: "get",
      url: serverUrl + "/personal-messages/unread-count",
      headers: {
        Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
      },
    });

    return data.data;
  }

  const unreadCount = useQuery("unread-count", getUnreadCount);

  return (
    <div className="h-full px-10 py-8">
      <ParentMainProfile />
      <AttendanceAppreciationBanner />
      <div className="py-10">
        <div className="bg-white w-full rounded-2xl bg-opacity-40 px-4 pb-4">
          <div className="flex items-center gap-8 p-4 py-6">
            <div
              onClick={() => setActiveTab("school-post")}
              className="text-2xl font-semibold cursor-pointer pb-1"
              style={{
                color: activeTab === "school-post" ? "#E46E31" : "#9ca3af",
                borderBottom:
                  activeTab === "school-post"
                    ? "3px solid #E46E31"
                    : "3px solid transparent",
              }}
            >
              School Post
            </div>
            <div
              onClick={() => setActiveTab("class-announcement")}
              className="text-2xl font-semibold cursor-pointer pb-1 flex items-center gap-2"
              style={{
                color:
                  activeTab === "class-announcement" ? "#E46E31" : "#9ca3af",
                borderBottom:
                  activeTab === "class-announcement"
                    ? "3px solid #E46E31"
                    : "3px solid transparent",
              }}
            >
              Class Update
              {unreadCount?.data?.result > 0 && (
                <span className="bg-red-500 text-white rounded-full px-2 py-0.5 text-xs font-bold">
                  {unreadCount?.data?.result}
                </span>
              )}
            </div>
            <div
              onClick={() => setActiveTab("birthday")}
              className="text-2xl font-semibold cursor-pointer pb-1"
              style={{
                color: activeTab === "birthday" ? "#E46E31" : "#9ca3af",
                borderBottom:
                  activeTab === "birthday"
                    ? "3px solid #E46E31"
                    : "3px solid transparent",
              }}
            >
              🎂 Birthday
            </div>
          </div>

          {activeTab === "school-post" ? (
            <div className="space-y-5">
              {announcement?.isSuccess &&
                announcement?.data
                  ?.slice()
                  ?.sort(
                    (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
                  )
                  ?.map((item, index) => {
                    return <FeedSection key={item?._id} data={item} />;
                  })}
            </div>
          ) : activeTab === "class-announcement" ? (
            <div>
              <TodayDaycareActivity isMontessoriEnabled={isMontessoriEnabled} />
              <ClassAnnouncementFeed />
            </div>
          ) : (
            <BirthdayFeed />
          )}
        </div>
      </div>
    </div>
  );
};

export default Index;
